import { useMutation } from '@tanstack/react-query';
import { DeploymentList } from 'kubernetes-types/apps/v1';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { rolloutRestartApplication } from '@/react/kubernetes/applications/queries/useRolloutRestartApplicationMutation';
import { HelmRelease } from '@/react/kubernetes/helm/types';
import { notifySuccess } from '@/portainer/services/notifications';
import { withError } from '@/react-tools/react-query';

import { confirm } from '@@/modals/confirm';
import { ModalType } from '@@/modals/Modal/types';
import { buildConfirmButton } from '@@/modals/utils';

import { useAddonEnvironmentId } from '../useAddonEnvironmentId';

import { addonNamespace } from './queries/useAddonRelease';

const SUCCESS_MESSAGE = 'The add-on may take a moment to come back up.';

/**
 * useAddonRestart rolls the add-on's deployments via kubectl rollout-restart,
 * re-creating the pods without a Helm upgrade (faster, and no async lifecycle to
 * poll). It restarts the deployments listed in the add-on's Helm release, and
 * only falls back to every deployment in its namespace when the release is
 * absent. Confirm the action with confirmAddonRestart before calling mutate.
 */
export function useAddonRestart(id: string) {
  const { environmentId } = useAddonEnvironmentId({ enabled: !!id });
  const namespace = addonNamespace(id);

  return useMutation(
    () => restartAddonDeployments(environmentId ?? 0, id, namespace),
    {
      onSuccess: () => notifySuccess('Restart started', SUCCESS_MESSAGE),
      ...withError('Unable to restart the add-on'),
    }
  );
}

/** Confirms a restart (the add-on may be unavailable). */
export function confirmAddonRestart(displayName: string) {
  return confirm({
    title: 'Restart add-on',
    modalType: ModalType.Warn,
    message: `Restart ${displayName}? It may be unavailable while it restarts.`,
    confirmButton: buildConfirmButton('Restart'),
  });
}

async function restartAddonDeployments(
  environmentId: EnvironmentId,
  releaseName: string,
  namespace: string
) {
  const names = await getDeploymentNames(environmentId, releaseName, namespace);
  if (names.length === 0) {
    throw new Error(
      'The add-on could not be found - it may have already been removed.'
    );
  }
  await Promise.all(
    names.map((name) =>
      rolloutRestartApplication(environmentId, namespace, 'Deployment', name)
    )
  );
}

// Prefer the deployments the Helm release owns (matching the resources table);
// only when the release can't be read does it fall back to every deployment in
// the namespace.
async function getDeploymentNames(
  environmentId: EnvironmentId,
  releaseName: string,
  namespace: string
): Promise<string[]> {
  const release = await getReleaseOrNull(environmentId, releaseName, namespace);
  if (release) {
    return (release.info?.resources ?? [])
      .filter((resource) => resource.kind === 'Deployment')
      .map((resource) => resource.metadata.name)
      .filter(Boolean);
  }
  return listNamespaceDeploymentNames(environmentId, namespace);
}

async function getReleaseOrNull(
  environmentId: EnvironmentId,
  releaseName: string,
  namespace: string
): Promise<HelmRelease | null> {
  try {
    const { data } = await axios.get<HelmRelease>(
      `/endpoints/${environmentId}/kubernetes/helm/${releaseName}`,
      { params: { namespace, showResources: true } }
    );
    return data;
  } catch {
    // A missing release is expected here — fall back to the namespace.
    return null;
  }
}

async function listNamespaceDeploymentNames(
  environmentId: EnvironmentId,
  namespace: string
): Promise<string[]> {
  try {
    const { data } = await axios.get<DeploymentList>(
      `/endpoints/${environmentId}/kubernetes/apis/apps/v1/namespaces/${namespace}/deployments`
    );
    return data.items
      .map((deployment) => deployment.metadata?.name)
      .filter((name): name is string => !!name);
  } catch (err) {
    throw parseAxiosError(err, 'Unable to list the add-on deployments');
  }
}
